import React, { useState, useCallback, useEffect, useRef } from "react";
import FluidLayoutEditor from "./FluidLayoutEditor";
import PageNavigator from "./PageNavigator";
import { trackEvent } from "../utils/analytics";
import {
  copyPageData, deletePageData, readPagePanelCount, readPageData, flushPendingPageSaves,
} from "../utils/pageStorage";
import { usePageThumbnails, pageSizeFor } from "../hooks/usePageThumbnails";
import { DEFAULT_STORYBOARD_TEMPLATE } from "../constants/storyboardTemplates";

// Comics and storyboards are separate projects, so opening one never
// disturbs the other
const PROJECT_STORAGE_KEYS = {
  comic: "striply_project",
  storyboard: "striply_storyboard_project",
};

const createDefaultPage = (index = 0) => ({
  id: `page-${Date.now()}-${index}`,
  createdAt: Date.now(),
});

/** The board a page was made from, to start a new page the same way. */
const boardSeedFromPage = (page) => {
  const data = readPageData(page?.id);
  if (data?.storyboard) {
    return { storyboard: data.storyboard, pageSizeKey: data.pageSizeKey, comicStyleKey: data.comicStyleKey };
  }
  return page?.board || DEFAULT_STORYBOARD_TEMPLATE.seed;
};

/**
 * @param {"comic"|"storyboard"} kind
 * @param {object} [seed] a board to open: a new storyboard project starts
 *   from it, an existing one gets it as a new page. Reported back through
 *   onSeedUsed so a reload doesn't add it again.
 */
const ProjectEditor = ({ layout, kind = "comic", seed = null, onSeedUsed }) => {
  const isStoryboard = kind === "storyboard";
  const projectStorageKey = PROJECT_STORAGE_KEYS[kind] || PROJECT_STORAGE_KEYS.comic;
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  // Panel counts per page id, read from each page's saved layout and kept
  // live by the open editor.
  const [panelCounts, setPanelCounts] = useState({});
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    setPanelCounts((prev) => {
      const next = {};
      pages.forEach((page) => {
        next[page.id] = prev[page.id] ?? readPagePanelCount(page.id);
      });
      return next;
    });
  }, [pages]);

  // Initialize: load project or create single page
  useEffect(() => {
    if (isInitialized) return;

    try {
      const saved = localStorage.getItem(projectStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed.pages) && parsed.pages.length > 0) {
          if (isStoryboard && seed) {
            // Opening a template into an existing board: it becomes a new page
            setPages([...parsed.pages, { ...createDefaultPage(parsed.pages.length), board: seed }]);
            setCurrentPageIndex(parsed.pages.length);
            trackEvent("storyboard_template_opened", { into: "existing" });
          } else {
            setPages(parsed.pages);
            setCurrentPageIndex(Math.min(parsed.currentPageIndex || 0, parsed.pages.length - 1));
          }
          if (seed && onSeedUsed) onSeedUsed();
          setIsInitialized(true);
          return;
        }
      }
    } catch (e) {
      console.warn("Failed to load project:", e);
    }

    if (isStoryboard) {
      setPages([{ ...createDefaultPage(0), board: seed || DEFAULT_STORYBOARD_TEMPLATE.seed }]);
      if (seed) trackEvent("storyboard_template_opened", { into: "new" });
      if (seed && onSeedUsed) onSeedUsed();
    } else {
      // Brand-new project: open on the sample page (FluidLayoutEditor builds it)
      setPages([{ ...createDefaultPage(0), starter: true }]);
    }
    setIsInitialized(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);

  // Auto-save
  useEffect(() => {
    if (!isInitialized || pages.length === 0) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(projectStorageKey, JSON.stringify({
          pages,
          currentPageIndex,
          savedAt: Date.now(),
          version: 1,
        }));
      } catch (e) {
        console.warn("Failed to save project:", e);
      }
    }, 1000);
    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, [pages, currentPageIndex, isInitialized, projectStorageKey]);

  const handleAddPage = useCallback(() => {
    const newPage = createDefaultPage(pages.length);
    // A new board page continues the board you're on (same frames and paper)
    if (isStoryboard) {
      flushPendingPageSaves();
      newPage.board = boardSeedFromPage(pages[currentPageIndex]);
    }
    setPages((prev) => [...prev, newPage]);
    setCurrentPageIndex(pages.length);
    trackEvent("page_added", { page_index: pages.length, kind });
  }, [pages, currentPageIndex, isStoryboard, kind]);

  const handleDeletePage = useCallback((index) => {
    const target = pages[index];
    if (!target || pages.length <= 1) return;
    setPages(pages.filter((_, i) => i !== index));
    // Stay on the page you were looking at; deleting an earlier page shifts it up
    setCurrentPageIndex((prev) => {
      if (index < prev) return prev - 1;
      return Math.min(prev, pages.length - 2);
    });
    deletePageData(target.id);
    trackEvent("page_deleted", { page_index: index });
  }, [pages]);

  const handleDuplicatePage = useCallback((index) => {
    const source = pages[index];
    if (!source) return;
    const dup = {
      ...JSON.parse(JSON.stringify(source)),
      id: `page-${Date.now()}-dup`,
      createdAt: Date.now(),
    };
    // Page content lives under the page id, so copy it to the new id
    copyPageData(source.id, dup.id);
    const next = [...pages];
    next.splice(index + 1, 0, dup);
    setPages(next);
    setCurrentPageIndex(index + 1);
  }, [pages]);

  const { thumbnails, refresh: refreshThumbnail } = usePageThumbnails(pages);

  // The open editor reports each save: update its count and redraw its thumbnail
  const handlePageSaved = useCallback((pageId, count) => {
    setPanelCounts((prev) => (prev[pageId] === count ? prev : { ...prev, [pageId]: count }));
    refreshThumbnail(pageId);
  }, [refreshThumbnail]);

  // ─── Whole-comic PDF ────────────────────────────────────────────────────
  const [exportProgress, setExportProgress] = useState(null);
  const handleExportAll = useCallback(async () => {
    flushPendingPageSaves();
    let shotsSoFar = 0;
    const pagesData = pages.map((page) => {
      const data = readPageData(page.id);
      const { width, height } = pageSizeFor(data);
      const cells = data?.cells || [];
      const shotOffset = shotsSoFar;
      if (data?.storyboard) shotsSoFar += cells.length;
      return {
        cells, pageWidthPx: width, pageHeightPx: height, comicStyleKey: data?.comicStyleKey,
        storyboard: data?.storyboard || null, shotOffset,
      };
    });
    try {
      setExportProgress({ page: 0, total: pagesData.length });
      const { exportProjectToPDF } = await import("../utils/pdfExport");
      await exportProjectToPDF(pagesData, isStoryboard ? "storyboard" : layout?.name || "comic", {
        dpi: 300,
        onProgress: (page, total) => setExportProgress({ page, total }),
      });
      trackEvent("export_project", { pages: pagesData.length, kind });
    } catch (e) {
      console.error("Failed to export project:", e);
      window.alert(`Couldn't export the ${isStoryboard ? "storyboard" : "comic"}. Please try again.`);
    } finally {
      setExportProgress(null);
    }
  }, [pages, layout, isStoryboard, kind]);

  const handleMovePage = useCallback((from, to) => {
    setPages((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setCurrentPageIndex(to);
  }, []);

  const currentPage = pages[currentPageIndex];

  if (!isInitialized || !currentPage) {
    return <div style={{ padding: 24 }}>Loading project...</div>;
  }

  // Shots number through the project: count the frames on earlier pages
  const shotOffset = isStoryboard
    ? pages.slice(0, currentPageIndex).reduce((sum, page) => sum + (panelCounts[page.id] || 0), 0)
    : 0;

  // Build a per-page layout object so FluidLayoutEditor uses page-specific storage
  const pageLayout = {
    ...layout,
    id: currentPage.id,
    starter: Boolean(currentPage.starter),
    board: isStoryboard ? currentPage.board || DEFAULT_STORYBOARD_TEMPLATE.seed : null,
    shotOffset,
    name: isStoryboard
      ? `storyboard-page-${currentPageIndex + 1}`
      : layout?.name ? `${layout.name} - Page ${currentPageIndex + 1}` : `Page ${currentPageIndex + 1}`,
  };

  return (
    <div style={{ display: "flex", height: "100%" }}>
      <PageNavigator
        pages={pages}
        panelCounts={panelCounts}
        thumbnails={thumbnails}
        onExportAll={handleExportAll}
        unit={isStoryboard ? "shot" : "panel"}
        exportAllLabel={isStoryboard ? "Export storyboard (PDF)" : undefined}
        exportProgress={exportProgress}
        currentPageIndex={currentPageIndex}
        onSelectPage={setCurrentPageIndex}
        onAddPage={handleAddPage}
        onDeletePage={handleDeletePage}
        onDuplicatePage={handleDuplicatePage}
        onMovePage={handleMovePage}
      />
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <FluidLayoutEditor
          key={currentPage.id}
          layout={pageLayout}
          onPanelCountChange={handlePageSaved}
        />
      </div>
    </div>
  );
};

export default ProjectEditor;
